/**
 * Utilitarios compartilhados para calculo e formatacao de faturamento.
 * Usar em: GraficoFaturamento, FonteReceita e outros componentes de relatorio.
 */

import api from './api';

export interface FlatPoint {
  mesIdx: number;
  semanaIdx: number;
  valor: number;
}

/**
 * Formata valor em centavos para string monetaria pt-BR.
 * Ex: 259900 -> "R$ 2.599,00"
 */
export function formatCurrency(centavos: number): string {
  return `R$ ${(centavos / 100).toLocaleString('pt-BR', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;
}

/**
 * Formata valor em centavos para label de eixo compacto.
 * Ex: 100000 -> "R$1k" | 50000 -> "R$500"
 */
export function formatAxisValue(centavos: number): string {
  const reais = centavos / 100;
  if (reais >= 1000) return `R$${(reais / 1000).toFixed(0)}k`;
  return `R$${reais.toFixed(0)}`;
}

/**
 * Transforma estrutura number[][] (mes x semanas) em lista plana de pontos.
 * Cada ponto contem: mesIdx, semanaIdx (base 0), valor em centavos.
 */
export function flattenData(data: number[][]): FlatPoint[] {
  const flat: FlatPoint[] = [];
  data.forEach((semanas, mesIdx) => {
    semanas.forEach((valor, semanaIdx) => {
      flat.push({ mesIdx, semanaIdx, valor });
    });
  });
  return flat;
}

export interface FaturamentoPonto {
  w: number;
  semanaIdx: number;
  nomeMes: string;
  total: number;
}

const MESES = ['Jan', 'Fev', 'Mar', 'Abr', 'Mai', 'Jun', 'Jul', 'Ago', 'Set', 'Out', 'Nov', 'Dez'];

/** Converte matriz de centavos (mes x semanas) em serie de pontos em reais, com semana global. */
export function toPontos(matriz: number[][]): FaturamentoPonto[] {
  const flat = flattenData(matriz);
  let w = 0;
  return flat.map((p) => {
    w += 1;
    return { w, semanaIdx: p.semanaIdx, nomeMes: MESES[p.mesIdx] ?? '', total: p.valor / 100 };
  });
}

export function money(reais: number): string {
  return 'R$ ' + Math.round(reais).toLocaleString('pt-BR');
}

interface FaturamentoGraficoResp { dados_ano_atual: number[][]; dados_ano_anterior: number[][]; }

interface FaturamentoGraficoCacheEntry {
  promise: Promise<{ atual: FaturamentoPonto[]; anterior: FaturamentoPonto[] }>;
  timestamp: number;
}

const FATURAMENTO_GRAFICO_CACHE_TTL_MS = 60_000;
const faturamentoGraficoCache = new Map<string, FaturamentoGraficoCacheEntry>();

/** Limpa o cache/dedupe em memoria de buscarFaturamentoGrafico (uso: testes, logout). */
export function clearFaturamentoGraficoCache(): void {
  faturamentoGraficoCache.clear();
}

async function fetchFaturamentoGrafico(
  ano: number, regiao?: string, feira?: string, incluirAnterior?: boolean,
): Promise<{ atual: FaturamentoPonto[]; anterior: FaturamentoPonto[] }> {
  const params: Record<string, string> = { ano: String(ano) };
  if (feira) params.feira = feira;
  else if (regiao) params.regiao = regiao;
  if (incluirAnterior) params.incluir_anterior = '1';
  const res = await api.get<FaturamentoGraficoResp>('/api/v1/relatorios/faturamento-grafico/', { params });
  return {
    atual: toPontos(res.data?.dados_ano_atual ?? []),
    anterior: toPontos(res.data?.dados_ano_anterior ?? []),
  };
}

/**
 * GET /api/v1/relatorios/faturamento-grafico/?ano=&regiao=&feira=&incluir_anterior=
 * Cache/dedupe em memoria (TTL 60s, escopo de modulo): varios componentes do
 * dashboard chamam esta funcao com os mesmos parametros ao montar juntos —
 * chamadas concorrentes ou repetidas dentro do TTL compartilham a mesma
 * Promise/resultado em vez de disparar uma requisicao HTTP cada uma.
 */
export async function buscarFaturamentoGrafico(
  ano: number, regiao?: string, feira?: string, incluirAnterior = false,
): Promise<{ atual: FaturamentoPonto[]; anterior: FaturamentoPonto[] }> {
  const key = `${ano}|${regiao ?? ''}|${feira ?? ''}|${incluirAnterior ? 1 : 0}`;
  const cached = faturamentoGraficoCache.get(key);
  if (cached && Date.now() - cached.timestamp < FATURAMENTO_GRAFICO_CACHE_TTL_MS) {
    return cached.promise;
  }
  const promise = fetchFaturamentoGrafico(ano, regiao, feira, incluirAnterior);
  faturamentoGraficoCache.set(key, { promise, timestamp: Date.now() });
  promise.catch(() => faturamentoGraficoCache.delete(key));
  return promise;
}

interface FonteReceitaResp { semana?: { numero?: number } }

/** GET /api/v1/relatorios/fontes_receita/?regiao= — semana mais recente com cobranca finalizada. */
export async function semanaMaisRecente(regiaoId: number): Promise<number | null> {
  try {
    const res = await api.get<FonteReceitaResp[]>('/api/v1/relatorios/fontes_receita/', { params: { regiao: regiaoId } });
    const lista = Array.isArray(res.data) ? res.data : [];
    return lista[0]?.semana?.numero ?? null;
  } catch {
    return null;
  }
}

export interface InfoAproveitamentoRegiaoResp {
  regiao_nome?: string;
  total_bancas_ativas?: number;
  bancas_pagas?: number;
  faturamento_real_reais?: number;
  faturamento_possivel_reais?: number;
  feiras?: {
    feira_id: number; feira_nome: string; total_bancas_ativas: number; bancas_pagas?: number;
    faturamento_possivel_reais: number; faturamento_real_reais?: number;
  }[];
}

/** GET /api/v1/relatorios/info_aproveitamento_regiao/?regiao=&semana= */
export async function buscarInfoAproveitamentoRegiao(
  regiaoId: number, semana: number | null,
): Promise<InfoAproveitamentoRegiaoResp> {
  const params: Record<string, string> = { regiao: String(regiaoId) };
  if (semana != null) params.semana = String(semana);
  const res = await api.get<InfoAproveitamentoRegiaoResp>('/api/v1/relatorios/info_aproveitamento_regiao/', { params });
  return res.data ?? {};
}

export interface Regiao { id: number; nome: string; }

/** GET /api/v1/regioes/ — nomes reais das regioes (ex.: Agreste, RMR). */
export async function buscarRegioes(): Promise<Regiao[]> {
  const res = await api.get<Regiao[] | { results: Regiao[] }>('/api/v1/regioes/');
  return Array.isArray(res.data) ? res.data : res.data?.results ?? [];
}

export interface TopRow {
  rank: number; label: string; valueFmt: string; barPct: string;
  deltaFmt: string; deltaBg: string; deltaFg: string;
}

/** Top 5 semanas (maior='desc', menor='asc') com delta percentual vs. media da serie. */
export function buildTopList(serie: FaturamentoPonto[], dir: 'desc' | 'asc'): TopRow[] {
  if (!serie.length) return [];
  const media = serie.reduce((a, x) => a + x.total, 0) / serie.length;
  const sorted = [...serie].sort((a, b) => (dir === 'desc' ? b.total - a.total : a.total - b.total)).slice(0, 5);
  const scaleMax = Math.max(...sorted.map((x) => x.total)) || 1;
  return sorted.map((x, i) => {
    const delta = media !== 0 ? ((x.total - media) / media) * 100 : 0;
    return {
      rank: i + 1,
      label: 'Semana ' + (x.semanaIdx + 1) + ' · ' + x.nomeMes,
      valueFmt: money(x.total),
      barPct: Math.round((x.total / scaleMax) * 100) + '%',
      deltaFmt: (delta >= 0 ? '+' : '−') + Math.abs(delta).toFixed(0) + '% vs. média',
      deltaBg: delta >= 0 ? 'rgba(34,197,94,.14)' : 'rgba(247,148,29,.16)',
      deltaFg: delta >= 0 ? '#15803d' : '#9A5B00',
    };
  });
}
